import { Data, Model, Utils } from '@singularsystems/neo-core';
import { AxiosPromise } from 'axios';
import { injectable } from 'inversify';
import { AppService, Types } from '../DomainTypes';
import DividendSummaryCriteria from '../Models/Dividends/Lookups/DividendSummaryCriteria';
import DividendSummaryLookup from '../Models/Dividends/Lookups/DividendSummaryLookup';
import DividendAccrualDetailCriteria from '../Models/Dividends/Lookups/DividendAccrualDetailCriteria';
import DividendAccrualDetailLookup from '../Models/Dividends/Lookups/DividendAccrualDetailLookup';
import DividendAccrualDetailResponse from '../Models/Dividends/Lookups/DividendAccrualDetailResponse';

export interface IDividendProcessingApiClient {

    /**
     * Gets a list of dividend accrual summary lookups
     */
    getDividendSummaries(request: Model.PartialPlainNonTrackedObject<DividendSummaryCriteria>): AxiosPromise<Array<Model.PlainObject<DividendSummaryLookup>>>;

    /** 
     * Fetches a page of dividend accruals for a specific dividend date, including other data applicable to the whole set which may be outside the page.
     */
    getDividendAccrualDetail(criteria: Model.PartialPlainNonTrackedObject<DividendAccrualDetailCriteria>): AxiosPromise<Model.PlainObject<DividendAccrualDetailResponse>>;

    /** 
     * Gets a list of dividend accrual detail lookups
     */
    getDividendAccrualDetailPage(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<DividendAccrualDetailCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<DividendAccrualDetailLookup>>>;

    /**
     * Generates dividends for dividend accruals that match the filter criteria
     * @param criteria The filter criteria for which accruals should generate dividends
     * @returns A Task
     */
    processDividendAccruals(criteria: Model.PartialPlainObject<DividendAccrualDetailCriteria>): AxiosPromise;

    // Client only properties / methods
}

@injectable()
export default class DividendProcessingApiClient extends Data.ApiClientBase implements IDividendProcessingApiClient {

    constructor (config = AppService.get(Types.App.Config)) {
        super(`${config.apiPath}/dividend-processing`);
    }

    public getDividendSummaries(request: Model.PartialPlainNonTrackedObject<DividendSummaryCriteria>): AxiosPromise<Array<Model.PlainObject<DividendSummaryLookup>>> {
        return this.axios.get(`${this.apiPath}/dividend-summaries?${Utils.getQueryString(request)}`);
    }

    public getDividendAccrualDetail(criteria: Model.PartialPlainNonTrackedObject<DividendAccrualDetailCriteria>): AxiosPromise<Model.PlainObject<DividendAccrualDetailResponse>> {
        return this.axios.get(`${this.apiPath}/dividend-accrual-details?${Utils.getQueryString(criteria)}`);
    }

    public getDividendAccrualDetailPage(request: Model.PartialPlainNonTrackedObject<Data.PageRequest<DividendAccrualDetailCriteria>>): AxiosPromise<Data.PageResult<Model.PlainObject<DividendAccrualDetailLookup>>> {
        return this.axios.get(`${this.apiPath}/dividend-accrual-details-page?${Utils.getQueryString(request)}`);
    }

    public processDividendAccruals(criteria: Model.PartialPlainObject<DividendAccrualDetailCriteria>): AxiosPromise {
        return this.axios.post(`${this.apiPath}/process`, criteria);
    }

    // Client only properties / methods
}